import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { T } from '../styles/tokens';
import { Button, Spinner, ListSkeleton, Textarea } from '../components/ui';
import { AdminGithub } from './AdminGithub';
import { AdminDatabase } from './AdminDatabase';
import { AdminSitemap } from './AdminSitemap';
import { AdminFlow } from './AdminFlow';
import { AdminDocsPage } from './AdminDocs';
import { AdminGithubActions } from './AdminGithubActions';
import { api } from '../api/client';
import { isAdmin } from '../stores/auth.store';

const FONT = "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace";

const TABS = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'files', label: 'Arquivos' },
  { id: 'database', label: 'Banco de Dados' },
  { id: 'sitemap', label: 'Sitemap' },
  { id: 'flow', label: 'Flow' },
  { id: 'docs', label: 'Documentação' },
  { id: 'actions', label: 'GitHub Actions' },
];

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
}

interface Machine {
  id: string;
  name: string;
  agentName: string;
  status: string;
  os: string;
  lastSeen: string;
  customerId?: string;
  hasGithub?: boolean;
}

interface Integration {
  id: string;
  customer_id: string;
  provider: string;
  key_name: string;
  email?: string;
}

// Tool metadata for action rendering
const _ti = (d: string, c: string, sz = 14) => `<svg width="${sz}" height="${sz}" viewBox="0 0 24 24" fill="none">${d}</svg>`;
const toolMeta = (name: string) => {
  const C = { amber: '#D4A053', purple: '#8B7EC8', blue: '#5B8AD4', cyan: '#4DB8C7', green: '#5DAA8C', gray: '#7E8A9A' };
  const map: Record<string, { icon: string; label: string; color: string }> = {
    exec: { icon: _ti(`<path d="M4 17l6-5-6-5M12 19h8" stroke="${C.amber}" stroke-width="2" stroke-linecap="round"/>`, C.amber), label: 'Terminal', color: C.amber },
    Edit: { icon: _ti(`<path d="M17 3a2.83 2.83 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" stroke="${C.purple}" stroke-width="1.5"/>`, C.purple), label: 'Editando', color: C.purple },
    edit: { icon: _ti(`<path d="M17 3a2.83 2.83 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" stroke="${C.purple}" stroke-width="1.5"/>`, C.purple), label: 'Editando', color: C.purple },
    Read: { icon: _ti(`<path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2zM22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" stroke="${C.blue}" stroke-width="1.5"/>`, C.blue), label: 'Lendo', color: C.blue },
    read: { icon: _ti(`<path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2zM22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" stroke="${C.blue}" stroke-width="1.5"/>`, C.blue), label: 'Lendo', color: C.blue },
    Write: { icon: _ti(`<path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="${C.purple}" stroke-width="1.5"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="${C.purple}" stroke-width="1.5" stroke-linecap="round"/>`, C.purple), label: 'Escrevendo', color: C.purple },
    write: { icon: _ti(`<path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="${C.purple}" stroke-width="1.5"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="${C.purple}" stroke-width="1.5" stroke-linecap="round"/>`, C.purple), label: 'Escrevendo', color: C.purple },
    web_search: { icon: _ti(`<circle cx="11" cy="11" r="8" stroke="${C.cyan}" stroke-width="1.5"/><path d="M21 21l-4.35-4.35" stroke="${C.cyan}" stroke-width="1.5" stroke-linecap="round"/>`, C.cyan), label: 'Pesquisa', color: C.cyan },
    web_fetch: { icon: _ti(`<circle cx="12" cy="12" r="10" stroke="${C.cyan}" stroke-width="1.5"/><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" stroke="${C.cyan}" stroke-width="1.5"/>`, C.cyan), label: 'Página', color: C.cyan },
    process: { icon: _ti(`<path d="M23 4v6h-6M1 20v-6h6" stroke="${C.amber}" stroke-width="1.5" stroke-linecap="round"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" stroke="${C.amber}" stroke-width="1.5" stroke-linecap="round"/>`, C.amber), label: 'Processo', color: C.amber },
    memory_search: { icon: _ti(`<path d="M12 2a7 7 0 017 7c0 2.38-1.19 4.47-3 5.74V17a1 1 0 01-1 1h-6a1 1 0 01-1-1v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 017-7zM9 21h6" stroke="${C.purple}" stroke-width="1.5" stroke-linecap="round"/>`, C.purple), label: 'Memória', color: C.purple },
    memory_get: { icon: _ti(`<path d="M12 2a7 7 0 017 7c0 2.38-1.19 4.47-3 5.74V17a1 1 0 01-1 1h-6a1 1 0 01-1-1v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 017-7zM9 21h6" stroke="${C.purple}" stroke-width="1.5" stroke-linecap="round"/>`, C.purple), label: 'Memória', color: C.purple },
    browser: { icon: _ti(`<rect x="2" y="3" width="20" height="14" rx="2" stroke="${C.cyan}" stroke-width="1.5"/><path d="M8 21h8M12 17v4" stroke="${C.cyan}" stroke-width="1.5" stroke-linecap="round"/>`, C.cyan), label: 'Browser', color: C.cyan },
    message: { icon: _ti(`<path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" stroke="${C.green}" stroke-width="1.5"/>`, C.green), label: 'Mensagem', color: C.green },
    cron: { icon: _ti(`<circle cx="12" cy="12" r="10" stroke="${C.amber}" stroke-width="1.5"/><path d="M12 6v6l4 2" stroke="${C.amber}" stroke-width="1.5" stroke-linecap="round"/>`, C.amber), label: 'Cron', color: C.amber },
    image: { icon: _ti(`<rect x="3" y="3" width="18" height="18" rx="2" stroke="${C.purple}" stroke-width="1.5"/><circle cx="8.5" cy="8.5" r="1.5" fill="${C.purple}"/><path d="M21 15l-5-5L5 21" stroke="${C.purple}" stroke-width="1.5" stroke-linecap="round"/>`, C.purple), label: 'Imagem', color: C.purple },
    tts: { icon: _ti(`<path d="M11 5L6 9H2v6h4l5 4V5z" stroke="${C.purple}" stroke-width="1.5"/><path d="M15.54 8.46a5 5 0 010 7.07" stroke="${C.purple}" stroke-width="1.5" stroke-linecap="round"/>`, C.purple), label: 'Áudio', color: C.purple },
    gateway: { icon: _ti(`<path d="M12 15.5A3.5 3.5 0 018.5 12 3.5 3.5 0 0112 8.5a3.5 3.5 0 013.5 3.5 3.5 3.5 0 01-3.5 3.5m7.43-2.53c.04-.32.07-.64.07-.97s-.03-.66-.07-1l2.11-1.63c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.3-.61-.22l-2.49 1c-.52-.4-1.08-.73-1.69-.98l-.38-2.65A.49.49 0 0014 2h-4c-.25 0-.46.18-.49.42l-.38 2.65c-.61.25-1.17.59-1.69.98l-2.49-1c-.23-.09-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64l2.11 1.65c-.04.32-.07.65-.07.97s.03.65.07.97l-2.11 1.66c-.19.15-.25.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1.01c.52.4 1.08.73 1.69.98l.38 2.65c.03.24.24.42.49.42h4c.25 0 .46-.18.49-.42l.38-2.65c.61-.25 1.17-.58 1.69-.98l2.49 1.01c.22.08.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.66z" stroke="${C.gray}" stroke-width="1"/>`, C.gray), label: 'Gateway', color: C.gray },
    sessions_spawn: { icon: _ti(`<path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 00-2.91-.09zM12 15l-3-3M22 2l-5 10-4-4L22 2z" stroke="${C.amber}" stroke-width="1.5" stroke-linecap="round"/>`, C.amber), label: 'Sub-agente', color: C.amber },
    compaction: { icon: _ti(`<path d="M21 12a9 9 0 11-6.22-8.56" stroke="${C.cyan}" stroke-width="2" stroke-linecap="round"/><path d="M21 3v6h-6" stroke="${C.cyan}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`, C.cyan), label: 'Compactando sessão', color: C.cyan },
  };
  return map[name] || { icon: _ti(`<path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" stroke="${C.gray}" stroke-width="1.5" stroke-linecap="round"/>`, C.gray), label: name, color: C.gray };
};

interface ActivityState {
  status: string;
  detail?: string;
  agentName?: string;
  streamingText?: string;
  tool?: { name: string; detail: string };
}

function renderContent(text: string): string {
  let html = text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/```(\w*)\n?([\s\S]*?)```/g, '<pre style="background:#111;padding:8px;border-radius:6px;overflow-x:auto;font-size:12px;margin:6px 0"><code>$2</code></pre>')
    .replace(/`([^`]+)`/g, `<code style="background:${T.bgSecondary};padding:1px 4px;border-radius:3px;font-size:12px">$1</code>`)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, `<a href="$2" target="_blank" style="color:${T.primary}">$1</a>`)
    .replace(/\n/g, '<br>');
  return html;
}

function timeAgo(d: string): string {
  const s = Math.floor((Date.now() - new Date(d + (d.includes('Z') ? '' : 'Z')).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function DashboardTab({ selectedMachine }: { selectedMachine: any }) {
  const [stats, setStats] = useState({ files: 0, tables: 0, routes: 0, docs: 0, deploys: 0, dbInserts: 0, dbUpdates: 0, successRate: 0 });
  const [dbActivity, setDbActivity] = useState<any[]>([]);
  const [recentFiles, setRecentFiles] = useState<any[]>([]);
  const [recentDocs, setRecentDocs] = useState<any[]>([]);
  const [actions, setActions] = useState<any[]>([]);
  const [sitemapRoutes, setSitemapRoutes] = useState<any[]>([]);
  const [expanded, setExpanded] = useState<{ type: string; data: any } | null>(null);
  const [expandContent, setExpandContent] = useState('');

  useEffect(() => {
    const loadAll = async () => {
      try {
        const [treeR, dbR, sitemapR, actionsR, tablesR] = await Promise.all([
          api.get<any>('/admin/github/tree').catch(() => null),
          api.get<any>('/admin/database/activity?limit=30').catch(() => null),
          api.get<any>('/admin/sitemap').catch(() => null),
          api.get<any>('/admin/github-actions/runs?per_page=30').catch(() => null),
          api.get<any>('/admin/database/tables').catch(() => null),
        ]);

        if (treeR?.tree) {
          const tree = treeR.tree;
          const allFiles = tree.filter((f: any) => f.type === 'file');
          const mdFiles = allFiles.filter((f: any) => f.path.endsWith('.md') && (f.path.startsWith('docs/') || f.path.startsWith('admin/docs/') || f.path.startsWith('apps/api/docs/') || f.path.startsWith('apps/web/public/admin/docs/') || f.path.startsWith('apps/web/public/docs/')));
          setStats(s => ({ ...s, files: allFiles.length, docs: mdFiles.length }));
          setRecentFiles(allFiles.slice(-30).reverse().map((f: any) => ({
            path: f.path, name: f.path.split('/').pop(), size: f.size || 0,
            ext: (f.path.split('.').pop() || '').toLowerCase(),
            folder: f.path.split('/').slice(0, -1).join('/'),
          })));
          setRecentDocs(mdFiles.map((f: any) => ({
            path: f.path, name: f.path.split('/').pop(),
            folder: f.path.split('/').slice(0, -1).join('/'), size: f.size || 0,
          })));
        }

        if (dbR?.activities) {
          const acts = dbR.activities;
          setDbActivity(acts);
          const inserts = acts.filter((a: any) => a.action === 'INSERT').length;
          const updates = acts.filter((a: any) => a.action === 'UPDATE').length;
          setStats(s => ({ ...s, dbInserts: inserts, dbUpdates: updates }));
        }

        if (sitemapR) {
          const fe = (sitemapR.frontend_routes || []).map((r: any) => ({ ...r, type: 'frontend' }));
          const ap = (sitemapR.api_routes || []).map((r: any) => ({ ...r, type: 'api' }));
          setSitemapRoutes([...fe, ...ap]);
          setStats(s => ({ ...s, routes: fe.length + ap.length }));
        }

        if (actionsR) {
          const runs = actionsR.runs || actionsR.workflow_runs || [];
          const mapped = runs.slice(0, 30).map((r: any) => ({
            id: r.id, name: r.name || 'Deploy',
            status: r.status, conclusion: r.conclusion,
            created_at: r.createdAt || r.created_at,
            head_sha: r.commitSha || (r.head_sha || '').substring(0, 7),
            message: r.commitMsg || r.head_commit?.message || r.display_title || '',
            url: r.url,
          }));
          setActions(mapped);
          const successes = mapped.filter((a: any) => a.conclusion === 'success').length;
          setStats(s => ({ ...s, deploys: mapped.length, successRate: mapped.length > 0 ? Math.round((successes / mapped.length) * 100) : 0 }));
        }

        if (tablesR?.tables) { setStats(s => ({ ...s, tables: tablesR.tables.length })); }
      } catch {}
    };
    loadAll();
    const iv = setInterval(loadAll, 8000);
    return () => clearInterval(iv);
  }, []);

  const handleExpand = async (type: string, item: any) => {
    setExpanded({ type, data: item });
    setExpandContent('');
    if (type === 'file' || type === 'doc') {
      try {
        const r = await api.get<any>(`/admin/github/file?path=${encodeURIComponent(item.path)}`);
        setExpandContent((r as any).content || 'Sem conteúdo');
      } catch { setExpandContent('Erro ao carregar'); }
    } else if (type === 'db') {
      try { setExpandContent(JSON.stringify(JSON.parse(item.row_data || '{}'), null, 2)); } catch { setExpandContent(item.row_data || '{}'); }
    } else if (type === 'action') {
      setExpandContent(`Status: ${item.status}\nConclusion: ${item.conclusion}\nCommit: ${item.head_sha}\n\n${item.message}`);
    }
  };

  const F = "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace";

  // Column feed renderer
  const FeedCol = ({ title, icon, items, renderItem, emptyText }: {
    title: string; icon: string; items: any[]; renderItem: (item: any, i: number) => any; emptyText?: string;
  }) => (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', background: T.bg, border: `1px solid ${T.bgSecondary}`, borderRadius: 4, overflow: 'hidden' }}>
      <div style={{ padding: '8px 10px', borderBottom: `1px solid ${T.bgSecondary}`, display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        {icon && <span style={{ fontSize: 12 }}>{icon}</span>}
        <span style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: F }}>{title}</span>
        <span style={{ fontSize: 10, color: T.textMuted, fontFamily: F, marginLeft: 'auto' }}>{items.length}</span>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {items.length === 0 ? (
          <div style={{ padding: 16, textAlign: 'center', color: T.textMuted, fontSize: 11, fontFamily: F }}>{emptyText || <ListSkeleton rows={3} />}</div>
        ) : items.map(renderItem)}
      </div>
    </div>
  );

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: T.bg, position: 'relative', overflow: 'hidden' }}>

      {/* ═══ EXPANDED OVERLAY ═══ */}
      {expanded && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 100, background: T.bg, display: 'flex', flexDirection: 'column', animation: 'fadeIn 150ms ease' }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid #222', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <Button variant="secondary" size="xs" onClick={() => setExpanded(null)}>← Voltar</Button>
            <span style={{ fontFamily: F, fontSize: 11, color: T.textMuted, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {expanded.data?.path || expanded.data?.name || expanded.data?.table_name || 'Preview'}
            </span>
          </div>
          <pre style={{ fontFamily: F, fontSize: 11, color: T.text, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, padding: 14, flex: 1, overflowY: 'auto', background: T.bg }}>{expandContent || <Spinner size="sm" text="Carregando..." />}</pre>
        </div>
      )}

      {/* ═══ TOP: STATS ROW ═══ */}
      <div style={{ display: 'flex', gap: 8, padding: '12px 12px 0', flexShrink: 0 }}>
        {[
          { label: 'Arquivos', value: stats.files, sub: `${stats.docs} docs`, color: T.primary },
          { label: 'Tabelas', value: stats.tables, sub: `${stats.dbInserts}↑ ${stats.dbUpdates}↻`, color: T.purple },
          { label: 'Rotas', value: stats.routes, sub: 'frontend + api', color: T.success },
          { label: 'Deploys', value: stats.deploys, sub: `${stats.successRate}% success`, color: stats.successRate >= 80 ? T.success : T.danger },
        ].map(s => (
          <div key={s.label} style={{ flex: 1, background: T.bg, border: `1px solid ${T.bgSecondary}`, borderRadius: 4, padding: '10px 12px' }}>
            <div style={{ fontSize: 10, color: T.textMuted, fontFamily: F, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{s.label}</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ fontSize: 22, fontWeight: 800, color: s.color, fontFamily: F, lineHeight: 1 }}>{s.value}</span>
              <span style={{ fontSize: 10, color: T.textMuted, fontFamily: F }}>{s.sub}</span>
            </div>
          </div>
        ))}
      </div>

      {/* ═══ DEPLOY TIMELINE (compact bar) ═══ */}
      <div style={{ padding: '8px 12px', flexShrink: 0 }}>
        <div style={{ background: T.bg, border: `1px solid ${T.bgSecondary}`, borderRadius: 4, padding: '8px 10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, fontFamily: F, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Deploy Timeline</span>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: T.success, animation: 'pulse 2s infinite' }} />
          </div>
          <div style={{ display: 'flex', gap: 2, height: 20, alignItems: 'flex-end' }}>
            {actions.slice(0, 30).map((a, i) => {
              const c = a.conclusion === 'success' ? T.success : a.conclusion === 'failure' ? T.danger : a.status === 'in_progress' ? T.primary : T.warning;
              return (
                <div key={i} onClick={() => handleExpand('action', a)} title={`${a.message?.substring(0, 50)} — ${a.conclusion || a.status}`}
                  style={{ flex: 1, height: a.conclusion === 'success' ? 16 : a.conclusion === 'failure' ? 20 : 10, background: c, borderRadius: 1, cursor: 'pointer', opacity: 0.8, transition: 'opacity 100ms' }}
                  onMouseEnter={(e: any) => e.currentTarget.style.opacity = '1'} onMouseLeave={(e: any) => e.currentTarget.style.opacity = '0.8'} />
              );
            })}
          </div>
        </div>
      </div>

      {/* ═══ 4 VERTICAL FEED COLUMNS ═══ */}
      <div style={{ flex: 1, display: 'flex', gap: 8, padding: '0 12px 12px', minHeight: 0, overflow: 'hidden' }}>

        {/* Col 1: Recent Files */}
        <FeedCol title="Arquivos" icon="" items={recentFiles} renderItem={(f, i) => (
          <div key={i} onClick={() => handleExpand('file', f)}
            style={{ padding: '6px 10px', borderBottom: `1px solid ${T.bgCard}`, cursor: 'pointer', transition: 'background 100ms' }}
            onMouseEnter={(e: any) => e.currentTarget.style.background = T.bgCard}
            onMouseLeave={(e: any) => e.currentTarget.style.background = 'transparent'}>
            <div style={{ fontSize: 11, fontWeight: 600, color: T.text, fontFamily: F, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</div>
            <div style={{ fontSize: 9, color: T.textMuted, fontFamily: F, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.folder} · {f.size ? `${(f.size / 1024).toFixed(1)}K` : ''}</div>
          </div>
        )} />

        {/* Col 2: DB Activity */}
        <FeedCol title="Banco de Dados" icon="" items={dbActivity} renderItem={(a, i) => {
          let summary = '';
          try { const d = JSON.parse(a.row_data || '{}'); summary = Object.entries(d).slice(0, 2).map(([k, v]) => `${k}: ${String(v).substring(0, 20)}`).join(' · '); } catch {}
          const ac = a.action === 'UPDATE' ? T.warning : a.action === 'DELETE' ? T.danger : T.success;
          return (
            <div key={i} onClick={() => handleExpand('db', a)}
              style={{ padding: '6px 10px', borderBottom: `1px solid ${T.bgCard}`, cursor: 'pointer', transition: 'background 100ms' }}
              onMouseEnter={(e: any) => e.currentTarget.style.background = T.bgCard}
              onMouseLeave={(e: any) => e.currentTarget.style.background = 'transparent'}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: ac, fontFamily: F }}>{a.action}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: T.text, fontFamily: F }}>{a.table_name}</span>
                <span style={{ fontSize: 9, color: T.textMuted, fontFamily: F, marginLeft: 'auto' }}>{timeAgo(a.created_at)}</span>
              </div>
              {summary && <div style={{ fontSize: 9, color: T.textMuted, fontFamily: F, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{summary}</div>}
            </div>
          );
        }} />

        {/* Col 3: Sitemap + Docs combined */}
        <FeedCol title="Rotas & Docs" icon="" items={[...sitemapRoutes.slice(0, 30), ...recentDocs.slice(0, 30).map(d => ({ ...d, _isDoc: true }))]} renderItem={(r, i) => {
          if ((r as any)._isDoc) {
            return (
              <div key={`doc-${i}`} onClick={() => handleExpand('doc', r)}
                style={{ padding: '6px 10px', borderBottom: `1px solid ${T.bgCard}`, cursor: 'pointer', transition: 'background 100ms' }}
                onMouseEnter={(e: any) => e.currentTarget.style.background = T.bgCard}
                onMouseLeave={(e: any) => e.currentTarget.style.background = 'transparent'}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontSize: 8, padding: '0 3px', background: T.warningBg, color: T.warning, borderRadius: 2, fontWeight: 700, fontFamily: F }}>DOC</span>
                  <span style={{ fontSize: 11, color: T.text, fontFamily: F }}>{r.name}</span>
                </div>
                <div style={{ fontSize: 9, color: T.textMuted, fontFamily: F, marginTop: 1 }}>{r.folder} · {(r.size / 1024).toFixed(1)}K</div>
              </div>
            );
          }
          return (
            <div key={`route-${i}`}
              style={{ padding: '6px 10px', borderBottom: `1px solid ${T.bgCard}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 8, padding: '0 3px', background: r.type === 'frontend' ? T.primaryBg : T.successBg, color: r.type === 'frontend' ? T.primary : T.success, borderRadius: 2, fontWeight: 700, fontFamily: F }}>{r.type === 'frontend' ? 'PAGE' : 'API'}</span>
                <span style={{ fontSize: 11, color: T.text, fontFamily: F, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.path}</span>
              </div>
              {(r.component || r.handler) && <div style={{ fontSize: 9, color: T.textMuted, fontFamily: F, marginTop: 1 }}>{r.component || r.handler}</div>}
            </div>
          );
        }} />

        {/* Col 4: GitHub Actions */}
        <FeedCol title="GitHub Actions" icon="" items={actions} renderItem={(a, i) => {
          const c = a.conclusion === 'success' ? T.success : a.conclusion === 'failure' ? T.danger : a.status === 'in_progress' ? T.primary : T.warning;
          return (
            <div key={i} onClick={() => handleExpand('action', a)}
              style={{ padding: '6px 10px', borderBottom: `1px solid ${T.bgCard}`, cursor: 'pointer', transition: 'background 100ms' }}
              onMouseEnter={(e: any) => e.currentTarget.style.background = T.bgCard}
              onMouseLeave={(e: any) => e.currentTarget.style.background = 'transparent'}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: c, flexShrink: 0 }} />
                <span style={{ fontSize: 11, fontWeight: 600, color: T.text, fontFamily: F, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{a.message?.substring(0, 40) || a.name}</span>
                <span style={{ fontSize: 9, color: T.textMuted, fontFamily: F, flexShrink: 0 }}>{a.head_sha}</span>
              </div>
              <div style={{ fontSize: 9, color: T.textMuted, fontFamily: F, marginTop: 2, display: 'flex', gap: 6 }}>
                <span style={{ color: c, fontWeight: 600 }}>{a.conclusion || a.status}</span>
                <span>{timeAgo(a.created_at)}</span>
              </div>
            </div>
          );
        }} />
      </div>

      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </div>
  );
}

function MobileBlock() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: T.bg, padding: 32 }}>
      <div style={{ textAlign: 'center', maxWidth: 360 }}>
        <div style={{ width: 48, height: 48, margin: '0 auto 16px', border: `2px solid ${T.textPrimary}`, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={T.textMuted} stroke-width="2"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
        </div>
        <div style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: "'SFMono-Regular', Consolas, monospace", marginBottom: 8 }}>Desktop Only</div>
        <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.5, fontFamily: "'SFMono-Regular', Consolas, monospace" }}>
          O Dev Hub requer uma tela maior para funcionar corretamente. Acesse pelo computador.
        </div>
      </div>
    </div>
  );
}

export function AdminDev() {
  const [hasAccess, setHasAccess] = useState(isAdmin.value);
  const [checkingAccess, setCheckingAccess] = useState(!isAdmin.value);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);
  const [active, setActive] = useState('dashboard');

  // Check plan feature
  useEffect(() => {
    if (isAdmin.value) return;
    api.get<any>('/subscription/me').then(res => {
      try { setHasAccess(!!JSON.parse(res?.plan?.features || '{}').dev_hub); } catch { }
    }).catch(() => {}).finally(() => setCheckingAccess(false));
  }, []);
  const [reloadKey, setReloadKey] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);

  // Mobile detection
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Remove main-content padding for full-bleed layout
  useEffect(() => {
    const main = document.querySelector('.main-content');
    if (main) main.classList.add('devhub-active');
    return () => { if (main) main.classList.remove('devhub-active'); };
  }, []);

  // Fullscreen: hide sidebar
  useEffect(() => {
    const sidebar = document.querySelector('.sidebar') as HTMLElement;
    if (!sidebar) return;
    if (fullscreen) {
      sidebar.style.display = 'none';
    } else {
      sidebar.style.display = '';
    }
    return () => { sidebar.style.display = ''; };
  }, [fullscreen]);

  // Deploy status
  interface DeployStatus {
    status: 'idle' | 'running' | 'success' | 'failure';
    conclusion?: string;
    name?: string;
    updatedAt?: string;
  }
  const [deployStatus, setDeployStatus] = useState<DeployStatus>({ status: 'idle' });

  // Unified: machines + integrations
  const [machines, setMachines] = useState<Machine[]>([]);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [selectedMachine, setSelectedMachine] = useState<Machine | null>(null);
  const [machinesLoading, setMachinesLoading] = useState(true);

  // Chat state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<any>(null);
  const [activity, setActivity] = useState<ActivityState>({ status: 'idle' });
  const activityTimerRef = useRef<any>(null);
  // prevActivityRef removed — wasStreaming check is inside pollActivity now

  // Load machines + integrations together
  useEffect(() => {
    (async () => {
      try {
        // Load both in parallel
        const [runnersRes, integrationsRes] = await Promise.all([
          api.get('/runner-v3/list').catch(() => null),
          api.get('/dev/integrations').catch(() => null),
        ]);

        // Parse integrations
        const ints: Integration[] = (integrationsRes as any)?.integrations || [];
        setIntegrations(ints);

        // Get unique customer_ids that have GitHub
        const githubCustomerIds = new Set(ints.map((i: Integration) => i.customer_id));

        // Parse runners
        const runners = (runnersRes as any)?.data?.runners || (runnersRes as any)?.runners || [];
        const list: Machine[] = runners.map((r: any) => {
          const isOnline = r.status === 'online' && r.last_seen_at &&
            (Date.now() - new Date(r.last_seen_at).getTime() < 180_000);
          return {
            id: r.id,
            name: r.name || 'Sem nome',
            agentName: r.agent_name || r.name || 'Agente',
            status: isOnline ? 'online' : 'offline',
            os: r.os || '',
            lastSeen: r.last_seen_at || '',
            customerId: r.customer_id || '',
            hasGithub: githubCustomerIds.has(r.customer_id),
          };
        });
        list.sort((a, b) => {
          if (a.status !== b.status) return a.status === 'online' ? -1 : 1;
          return a.name.localeCompare(b.name);
        });
        setMachines(list);

        // Auto-select saved or first online
        const savedId = localStorage.getItem('devhub_selectedMachine');
        const saved = savedId ? list.find(m => m.id === savedId) : null;
        const initial = saved || list.find(m => m.status === 'online') || list[0] || null;
        if (initial) handleSelectMachine(initial, ints);
      } catch {} finally { setMachinesLoading(false); }
    })();
  }, []);

  // Poll GitHub Actions status every 15s
  useEffect(() => {
    const checkDeploy = async () => {
      try {
        const data = await api.get<any>('/admin/github-actions/runs');
        const runs = (data as any)?.runs || [];
        if (runs.length === 0) { setDeployStatus({ status: 'idle' }); return; }
        const latest = runs[0];
        const updatedAt = latest.updated_at || latest.created_at || '';
        const ageMs = Date.now() - new Date(updatedAt).getTime();
        if (latest.status === 'in_progress' || latest.status === 'queued') {
          setDeployStatus({ status: 'running', name: latest.name, updatedAt });
        } else if (latest.conclusion === 'success' && ageMs < 120_000) {
          setDeployStatus({ status: 'success', name: latest.name, updatedAt });
        } else if (latest.conclusion === 'failure' && ageMs < 120_000) {
          setDeployStatus({ status: 'failure', name: latest.name, updatedAt });
        } else {
          setDeployStatus({ status: 'idle' });
        }
      } catch { setDeployStatus({ status: 'idle' }); }
    };
    checkDeploy();
    const interval = setInterval(checkDeploy, 15_000);
    return () => clearInterval(interval);
  }, []);

  // When selecting a machine, also set the active GitHub repo for that agent
  const handleSelectMachine = async (machine: Machine, ints?: Integration[]) => {
    setSelectedMachine(machine);
    localStorage.setItem('devhub_selectedMachine', machine.id);

    // Find GitHub integration for this machine's customer
    const allInts = ints || integrations;
    const customerInts = allInts.filter(i => i.customer_id === machine.customerId && i.provider === 'github');
    const hasToken = customerInts.some(i => i.key_name === 'token');

    if (hasToken) {
      // Switch active GitHub repo to this customer's integration
      try {
        await api.put('/admin/github-actions/active-repo', {
          owner: '', repo: '', branch: 'main',
          source: 'customer_integration',
          sourceId: machine.customerId,
        });
      } catch {}
    }

    setReloadKey(k => k + 1);
  };

  // Load messages when machine changes
  const loadMessages = useCallback(async () => {
    if (!selectedMachine) return;
    try {
      const data = await api.get<any>(`/runner-v3/${selectedMachine.id}/chat/messages?limit=100`);
      const rawMsgs = (data as any)?.data?.messages || (data as any)?.messages || [];
      const filtered: ChatMessage[] = rawMsgs
        .filter((m: any) => m.role === 'user' || (m.role === 'assistant' && m.content?.trim()))
        .map((m: any) => ({ id: m.id, role: m.role, content: m.content, timestamp: m.timestamp }));
      setMessages(filtered);
    } catch {}
  }, [selectedMachine]);

  useEffect(() => {
    if (selectedMachine) {
      setChatLoading(true);
      loadMessages().finally(() => setChatLoading(false));
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(loadMessages, 5000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [selectedMachine, loadMessages]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Activity polling — mirror Chat.tsx pattern exactly
  const activityRef = useRef(activity);
  activityRef.current = activity;
  useEffect(() => {
    if (!selectedMachine) return;
    const pollActivity = () => {
      api.get<any>(`/runner-v3/${selectedMachine.id}/chat/activity`).then(d => {
        const act = (d as any)?.data || (d as any) || {};
        console.log('[DevHub Activity]', selectedMachine.id, JSON.stringify(act).substring(0, 200));
        if (act?.status && act.status !== 'offline' && act.status !== 'online') {
          console.log('[DevHub Activity ACTIVE]', act.status);
        }
        if (act?.status) {
          const wasStreaming = activityRef.current.status === 'streaming';
          setActivity(act);
          if (act.status === 'streaming') {
            chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }
          if (wasStreaming && act.status !== 'streaming') {
            loadMessages();
          }
        }
      }).catch(() => {});
    };
    const scheduleNext = () => {
      const cur = activityRef.current;
      const isActive = cur.status === 'streaming' || cur.status === 'tool' || cur.status === 'thinking';
      const interval = isActive ? 800 : 3000;
      activityTimerRef.current = setTimeout(() => { pollActivity(); scheduleNext(); }, interval);
    };
    pollActivity();
    scheduleNext();
    return () => { if (activityTimerRef.current) clearTimeout(activityTimerRef.current); };
  }, [selectedMachine]);

  // Auto-scroll and reload are now handled inside pollActivity above

  const sendMessage = async () => {
    if (!selectedMachine || !inputText.trim() || sending) return;
    const text = inputText.trim();
    setInputText('');
    setSending(true);
    const optMsg: ChatMessage = { id: `opt_${Date.now()}`, role: 'user', content: text, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, optMsg]);
    try {
      await api.post<any>(`/runner-v3/${selectedMachine.id}/chat/send`, { message: text });
    } catch {}
    setSending(false);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const formatTime = (ts: string) => {
    try { return new Date(ts).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; }
  };

  const hasGithub = selectedMachine?.hasGithub ?? false;

  if (checkingAccess) return <div style={{ padding: 40, color: T.textMuted, fontFamily: FONT }}>Verificando acesso...</div>;
  if (!hasAccess) return (
    <div style={{ padding: '60px 24px', textAlign: 'center', fontFamily: FONT, color: T.text, maxWidth: 500, margin: '0 auto' }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}></div>
      <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Dev Hub — Plano Business ou Enterprise</h2>
      <p style={{ color: T.textMuted, fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>
        O Dev Hub é uma funcionalidade exclusiva dos planos <strong style={{ color: T.purpleHover }}>Business</strong> e <strong style={{ color: T.warning }}>Enterprise</strong>.
        Faça upgrade do seu plano para acessar APIs, banco de dados, deploy e mais.
      </p>
      <a href="/profile/plano" style={{ display: 'inline-block', padding: '10px 24px', borderRadius: 8, background: 'linear-gradient(135deg, #7c3aed, #2563eb)', color: T.text, fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>
        Ver Planos
      </a>
    </div>
  );
  if (isMobile) return <MobileBlock />;

  return (
    <div style={{ display: 'flex', height: fullscreen ? '100vh' : '100%', background: T.bg, color: T.text, fontFamily: FONT }}>
      {/* LEFT PANEL - 70% */}
      <div style={{ flex: '0 0 70%', display: 'flex', flexDirection: 'column', borderRight: '1px solid #222', minWidth: 0 }}>
        {/* TAB BAR */}
        <div style={{ height: 44, display: 'flex', alignItems: 'center', gap: 0, padding: '0 8px', background: T.bgSecondary, borderBottom: '1px solid #222', flexShrink: 0, overflowX: 'auto' }}>
          {TABS.map(t => (
            <Button variant="ghost"
              key={t.id}
              onClick={() => setActive(t.id)}
              style={{
                padding: '0 16px', height: '44px', background: 'transparent',
                color: active === t.id ? T.primary : T.textSecondary,
                border: 'none', borderBottom: active === t.id ? `2px solid ${T.primary}` : '2px solid transparent',
                cursor: 'pointer', fontFamily: FONT, fontSize: 13,
                fontWeight: active === t.id ? 600 : 400, whiteSpace: 'nowrap' as const,
                transition: 'color 0.15s, border-color 0.15s',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <span>{t.label}</span>
              {t.id === 'actions' && deployStatus.status !== 'idle' && (
                <span style={{
                  width: 8, height: 8, borderRadius: '50%', display: 'inline-block',
                  background: deployStatus.status === 'running' ? T.warning :
                              deployStatus.status === 'success' ? T.success : T.danger,
                  boxShadow: deployStatus.status === 'running' ? `0 0 6px ${T.warningBgStrong}` :
                             deployStatus.status === 'success' ? `0 0 6px ${T.successBgStrong}` :
                             `0 0 6px ${T.danger}`,
                }} />
              )}
            </Button>
          ))}
          <div style={{ flex: 1 }} />
          <Button variant="ghost"
            onClick={() => setFullscreen(!fullscreen)}
            title={fullscreen ? 'Sair do fullscreen' : 'Fullscreen'}
            style={{
              background: fullscreen ? T.primaryBg : 'transparent',
              border: 'none', color: fullscreen ? T.primary : T.textMuted,
              cursor: 'pointer', padding: '0 10px', height: 44,
              display: 'flex', alignItems: 'center', fontSize: 16,
              transition: 'color 0.15s, background 0.15s',
              flexShrink: 0, borderRadius: 0,
            }}
          >
            {fullscreen ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="4 14 4 20 10 20" /><polyline points="20 10 20 4 14 4" />
                <line x1="14" y1="10" x2="21" y2="3" /><line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" />
                <line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            )}
          </Button>
        </div>

        {/* TAB CONTENT */}
        <div style={{ flex: 1, overflow: 'hidden' }}>
          {!selectedMachine ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: T.textMuted, fontSize: 14 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 18, marginBottom: 12, color: T.textMuted }}>Selecione um agente para começar</div>
              </div>
            </div>
          ) : !hasGithub ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: T.textMuted, fontSize: 14 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 16, marginBottom: 8 }}>Nenhum GitHub conectado neste agente</div>
                <div style={{ fontSize: 12, color: T.textMuted }}>
                  Conecte um GitHub em Integrações para usar as ferramentas de desenvolvimento
                </div>
              </div>
            </div>
          ) : (
            <>
              {active === 'dashboard' && <DashboardTab selectedMachine={selectedMachine} />}
              {active === 'files' && <AdminGithub key={reloadKey} />}
              {active === 'database' && <AdminDatabase key={reloadKey} />}
              {active === 'sitemap' && <AdminSitemap key={reloadKey} />}
              {active === 'flow' && <AdminFlow key={reloadKey} />}
              {active === 'docs' && <AdminDocsPage key={reloadKey} />}
              {active === 'actions' && <AdminGithubActions key={reloadKey} />}
            </>
          )}
        </div>
      </div>

      {/* RIGHT PANEL - 30% */}
      <div style={{ flex: '0 0 30%', display: 'flex', flexDirection: 'column', background: T.bg, minWidth: 0 }}>
        {/* UNIFIED AGENT SELECTOR */}
        <div style={{ padding: '12px', borderBottom: '1px solid #222', flexShrink: 0 }}>
          <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 6, textTransform: 'uppercase' as const, letterSpacing: '0.5px', fontWeight: 600 }}>Agente</div>
          {machinesLoading ? (
            <div style={{ color: T.textMuted, fontSize: 12, padding: '8px 0' }}>Carregando agentes...</div>
          ) : (
            <select
              value={selectedMachine?.id || ''}
              onChange={(e: any) => {
                const m = machines.find(m => m.id === e.target.value);
                if (m) handleSelectMachine(m);
              }}
              style={{ background: T.bg, border: `1px solid ${T.border}`, color: T.text, borderRadius: 6, padding: '8px 12px', width: '100%', fontFamily: FONT, fontSize: 13, outline: 'none', cursor: 'pointer' }}
            >
              {machines.length === 0 && <option value="">Nenhum agente</option>}
              {machines.map(m => (
                <option key={m.id} value={m.id}>
                  {m.status === 'online' ? '● ' : '○ '}{m.agentName} {m.hasGithub ? '· GitHub' : ''}
                </option>
              ))}
            </select>
          )}
          {selectedMachine && (
            <div style={{ marginTop: 6, fontSize: 11 }}>
              {deployStatus.status === 'running' && (
                <div style={{
                  background: T.warningBg, border: `1px solid ${T.warningBgStrong}`,
                  borderRadius: 4, padding: '6px 10px', color: T.warning,
                  fontFamily: FONT, display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.warning, display: 'inline-block' }} />
                  Deploy em andamento...
                </div>
              )}
              {deployStatus.status === 'success' && (
                <div style={{
                  background: T.successBg, border: `1px solid ${T.successBgStrong}`,
                  borderRadius: 4, padding: '6px 10px', color: T.success,
                  fontFamily: FONT, display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.success, display: 'inline-block' }} />
                  Deploy concluído
                  {deployStatus.updatedAt && (
                    <span style={{ color: T.textMuted, marginLeft: 'auto' }}>
                      {new Date(deployStatus.updatedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
              )}
              {deployStatus.status === 'failure' && (
                <div style={{
                  background: T.dangerBg, border: `1px solid ${T.dangerBg}`,
                  borderRadius: 4, padding: '6px 10px', color: T.danger,
                  fontFamily: FONT, display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.danger, display: 'inline-block' }} />
                  Deploy falhou
                  {deployStatus.updatedAt && (
                    <span style={{ color: T.textMuted, marginLeft: 'auto' }}>
                      {new Date(deployStatus.updatedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
              )}
              {deployStatus.status === 'idle' && hasGithub && (
                <div style={{ color: T.textDisabled, fontSize: 10 }}>Nenhum deploy recente</div>
              )}
              {!hasGithub && (
                <div style={{ color: T.danger, fontSize: 10 }}>● Sem GitHub conectado</div>
              )}
            </div>
          )}
        </div>

        {/* CHAT */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {chatLoading && <Spinner size="sm" text="Carregando..." />}
            {!chatLoading && messages.length === 0 && (
              <div style={{ textAlign: 'center', color: T.textMuted, fontSize: 12, marginTop: 20 }}>
                {selectedMachine ? 'Nenhuma mensagem ainda' : 'Selecione um agente'}
              </div>
            )}
            {messages.map(msg => {
              const isUser = msg.role === 'user';
              return (
                <div key={msg.id} style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
                  <div style={{
                    maxWidth: '85%', padding: '8px 12px', borderRadius: 8,
                    background: isUser ? '#1e3a5f' : T.bgSecondary,
                    fontSize: 13, lineHeight: '1.5', wordBreak: 'break-word' as const,
                  }}>
                    <div dangerouslySetInnerHTML={{ __html: renderContent(msg.content) }} />
                    <div style={{ fontSize: 10, color: T.textMuted, marginTop: 4, textAlign: isUser ? 'right' as const : 'left' as const }}>
                      {formatTime(msg.timestamp)}
                    </div>
                  </div>
                </div>
              );
            })}
            {/* Activity: Streaming */}
            {activity.status === 'streaming' && activity.streamingText && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{
                  maxWidth: '85%', padding: '8px 12px', borderRadius: 8,
                  background: T.purpleBg,
                  border: `1px solid ${T.purple}`,
                  fontSize: 13, lineHeight: '1.5', wordBreak: 'break-word' as const,
                }}>
                  <div dangerouslySetInnerHTML={{ __html: renderContent(activity.streamingText) }} />
                  <span style={{ color: T.purple, animation: 'blink 1s infinite' }}>▊</span>
                </div>
              </div>
            )}
            {/* Activity: Tool */}
            {activity.status === 'tool' && activity.tool && (() => {
              const meta = toolMeta(activity.tool!.name);
              const detail = activity.tool!.detail?.replace(/\/data\/\.openclaw\/workspace\//g, '') || '';
              return (
                <div style={{ display: 'flex', padding: '2px 0' }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '6px 12px', borderRadius: 12,
                    background: `${meta.color}0a`, border: `1px solid ${meta.color}18`,
                  }}>
                    <span style={{
                      width: 24, height: 24, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', borderRadius: 6, background: `${meta.color}15`,
                    }} dangerouslySetInnerHTML={{ __html: meta.icon }} />
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: meta.color }}>{meta.label}</span>
                        <span style={{
                          display: 'inline-block', width: 8, height: 8,
                          border: `2px solid ${meta.color}`, borderTopColor: 'transparent',
                          borderRadius: '50%', animation: 'spin 0.8s linear infinite',
                        }} />
                      </div>
                      {detail && (
                        <div style={{
                          fontSize: 10, color: T.textMuted, fontFamily: FONT,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          maxWidth: 220, marginTop: 1,
                        }}>{detail}</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
            {/* Activity: Thinking */}
            {activity.status === 'thinking' && (
              <div style={{ display: 'flex', padding: '2px 0' }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 12px', borderRadius: 12,
                  background: T.purpleBg, border: `1px solid ${T.purpleBg}`,
                }}>
                  <div style={{ display: 'flex', gap: 3 }}>
                    {[0, 0.15, 0.3].map((d, j) => (
                      <span key={j} style={{
                        width: 4, height: 4, borderRadius: '50%', background: T.purple,
                        animation: `pulse 1s ease-in-out ${d}s infinite`,
                      }} />
                    ))}
                  </div>
                  <span style={{ fontSize: 11, color: T.purple, fontWeight: 500 }}>
                    {(activity as any).detail || 'Pensando...'}
                  </span>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input */}
          <div style={{ padding: '8px 12px', borderTop: '1px solid #222', display: 'flex', gap: 8, flexShrink: 0 }}>
            <Textarea
              value={inputText}
              onInput={(e: any) => {
                setInputText(e.target.value);
                // Auto-resize
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 160) + 'px';
              }}
              onKeyDown={handleKeyDown}
              placeholder={selectedMachine ? 'Mensagem...' : 'Selecione um agente'}
              disabled={!selectedMachine || sending}
              rows={1}
              style={{
                flex: 1, background: T.bg, border: `1px solid ${T.border}`, color: T.text,
                borderRadius: 6, padding: '8px 12px', fontFamily: FONT, fontSize: 13,
                resize: 'none' as const, outline: 'none', minHeight: 36, maxHeight: 160,
                overflow: 'auto', lineHeight: '1.4',
              }}
            />
            <Button variant="ghost"
              onClick={sendMessage}
              disabled={!selectedMachine || !inputText.trim() || sending}
              style={{
                background: inputText.trim() ? T.primary : T.textPrimary,
                color: T.text, border: 'none', borderRadius: 6, padding: '0 14px',
                cursor: inputText.trim() ? 'pointer' : 'default',
                fontSize: 14, fontWeight: 600, flexShrink: 0,
                opacity: inputText.trim() ? 1 : 0.5,
                transition: 'background 0.15s, opacity 0.15s',
              }}
            >
              {sending ? '...' : '↑'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
